import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Icon from "../AppIcon";
import { useLanguage } from "../../context/LanguageContext";

const ROUTE_MAP: Record<string, string> = {
  home: "/", about: "/about", academy: "/academy", trade: "/trade",
  vault: "/vault", testimonials: "/testimonials", help: "/help",
  contact: "/contact", privacy: "/privacy", terms: "/terms",
  login: "/login", register: "/register",
};

const SOCIAL_LINKS = [
  { name: "Twitter", icon: "Twitter", href: "#" },
  { name: "Github", icon: "Github", href: "#" },
  { name: "Linkedin", icon: "Linkedin", href: "#" },
  { name: "Youtube", icon: "Youtube", href: "#" },
];

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();

  const handleNavigate = (page: string) => {
    const path = ROUTE_MAP[page] || `/${page}`;
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const FOOTER_SECTIONS = [
    {
      title: t('footer.explore'), icon: "Compass",
      links: [
        { label: t('nav.home'), page: "home" },
        { label: t('nav.trade'), page: "trade" },
        { label: t('nav.vault'), page: "vault" },
        { label: t('footer.reviews'), page: "testimonials" },
        { label: t('nav.about'), page: "about" },
      ],
    },
    {
      title: t('footer.learn'), icon: "BookOpen",
      links: [
        { label: t('nav.academy'), page: "academy" },
        { label: t('footer.learningJourney'), page: "academy" },
        { label: t('footer.cryptoGlossary'), page: "academy" },
        { label: t('footer.videoCourses'), page: "academy" },
        { label: t('support.faq'), page: "academy" },
      ],
    },
    {
      title: t('footer.support'), icon: "LifeBuoy",
      links: [
        { label: t('footer.helpCenter'), page: "help" },
        { label: t('support.contact'), page: "contact" },
        { label: t('footer.privacy'), page: "privacy" },
        { label: t('footer.terms'), page: "terms" },
      ],
    },
  ];

  const STATS = [
    { value: "100K+", label: t('footer.activeUsers'), icon: "Users" },
    { value: "50+", label: t('footer.expertCourses'), icon: "BookOpen" },
    { value: "$2B+", label: t('footer.assetsManaged'), icon: "Wallet" },
    { value: "24/7", label: t('footer.liveSupport'), icon: "Headphones" },
  ];

  return (
    <footer className="relative overflow-hidden">
      <div className="h-px bg-gradient-to-r from-transparent via-accent/40 to-transparent" />

      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-accent/[0.03] rounded-full blur-3xl" />
        <div className="absolute -bottom-60 -left-40 w-[600px] h-[600px] bg-primary/[0.04] rounded-full blur-3xl" />
      </div>

      <div className="relative">
        {/* CTA BANNER */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
          <div className="relative overflow-hidden bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-xl border border-primary/10 rounded-3xl p-8 md:p-12 shadow-card">
            <div className="absolute -top-20 -right-20 w-56 h-56 bg-accent/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-48 h-48 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />

            <div className="relative flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="text-center lg:text-left max-w-xl">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent/10 border border-accent/20 rounded-full text-xs font-semibold text-accent mb-4">
                  <Icon name="TrendingUp" size={12} />
                  {isAuthenticated ? t('footer.ctaBadgeAuth') : t('footer.ctaBadge')}
                </div>
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 leading-tight">
                  {isAuthenticated ? (
                    <>{t('footer.ctaTitleAuth1')}{" "}<span className="text-transparent bg-clip-text bg-gradient-to-r from-accent via-accent-light to-accent">{t('footer.ctaTitleAuth2')}</span></>
                  ) : (
                    <>{t('footer.ctaTitle1')}{" "}<span className="text-transparent bg-clip-text bg-gradient-to-r from-accent via-accent-light to-accent">{t('footer.ctaTitle2')}</span></>
                  )}
                </h3>
                <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                  {isAuthenticated ? t('footer.ctaDescAuth') : t('footer.ctaDesc')}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0">
                {isAuthenticated ? (
                  <>
                    <button onClick={() => navigate("/dashboard")} className="group inline-flex items-center gap-2.5 px-8 py-3.5 text-sm font-semibold bg-accent hover:bg-accent-dark text-accent-foreground rounded-xl transition-all shadow-glow hover:shadow-glow-lg">
                      <Icon name="LayoutDashboard" size={18} />
                      {t('footer.openDashboard')}
                      <Icon name="ArrowRight" size={16} className="transition-transform group-hover:translate-x-0.5" />
                    </button>
                    <button onClick={() => navigate("/dashboard?section=investment-plans")} className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-medium bg-card/60 border border-primary/10 text-foreground hover:bg-card/80 hover:border-accent/30 rounded-xl transition-all">
                      <Icon name="PieChart" size={16} className="text-accent" />
                      {t('footer.viewInvestments')}
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => handleNavigate("register")} className="group inline-flex items-center gap-2.5 px-8 py-3.5 text-sm font-semibold bg-accent hover:bg-accent-dark text-accent-foreground rounded-xl transition-all shadow-glow hover:shadow-glow-lg">
                      <Icon name="Rocket" size={18} />
                      {t('footer.getStartedFree')}
                      <Icon name="ArrowRight" size={16} className="transition-transform group-hover:translate-x-0.5" />
                    </button>
                    <button onClick={() => handleNavigate("academy")} className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-medium bg-card/60 border border-primary/10 text-foreground hover:bg-card/80 hover:border-accent/30 rounded-xl transition-all">
                      <Icon name="Play" size={16} className="text-accent" />
                      {t('footer.watchDemo')}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* STATS BAR */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {STATS.map((stat) => (
              <div key={stat.label} className="group flex items-center gap-3 p-4 bg-card/30 backdrop-blur-sm border border-primary/10 rounded-xl hover:border-accent/20 hover:bg-card/50 transition-all">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent/20 to-accent/5 border border-accent/15 flex items-center justify-center shrink-0">
                  <Icon name={stat.icon} size={18} className="text-accent" />
                </div>
                <div>
                  <div className="text-lg font-bold text-foreground font-data leading-tight">{stat.value}</div>
                  <div className="text-xs text-muted-foreground">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* MAIN FOOTER GRID */}
        <div className="border-t border-primary/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-10 md:gap-8">
              <div className="md:col-span-2">
                <button onClick={() => handleNavigate("home")} className="group hover:opacity-80 transition-opacity focus:outline-none mb-5">
                  <img src="/logo.png" alt="Acebarter" width="115" height="32" className="h-8" />
                </button>
                <p className="text-muted-foreground text-sm leading-relaxed mb-6 max-w-xs">
                  {t('footer.brandDesc')}
                </p>
                <div className="flex items-center gap-2.5">
                  {SOCIAL_LINKS.map((social) => (
                    <a key={social.name} href={social.href} aria-label={social.name} className="group/social w-10 h-10 bg-card/50 border border-primary/10 rounded-xl hover:bg-accent/10 hover:border-accent/30 flex items-center justify-center transition-all">
                      <Icon name={social.icon} size={17} className="text-muted-foreground group-hover/social:text-accent transition-colors" />
                    </a>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-card/30 border border-primary/10 rounded-xl">
                  <p className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
                    <Icon name="Mail" size={13} className="text-accent" />
                    {t('footer.stayUpdated')}
                  </p>
                  <div className="flex gap-2">
                    <input type="email" placeholder={t('footer.emailPlaceholder')} className="flex-1 bg-background/60 border border-primary/10 rounded-lg px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent/30 transition-colors" />
                    <button className="px-3 py-2 bg-accent hover:bg-accent-dark text-accent-foreground rounded-lg text-xs font-semibold transition-colors shrink-0">
                      <Icon name="Send" size={13} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="md:col-span-4 grid grid-cols-2 sm:grid-cols-3 gap-8">
                {FOOTER_SECTIONS.map((section) => (
                  <div key={section.title}>
                    <div className="flex items-center gap-2 mb-5">
                      <div className="w-7 h-7 rounded-lg bg-accent/10 border border-accent/15 flex items-center justify-center">
                        <Icon name={section.icon} size={13} className="text-accent" />
                      </div>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-foreground">{section.title}</h4>
                    </div>
                    <ul className="space-y-3">
                      {section.links.map((link) => (
                        <li key={link.label}>
                          <button onClick={() => handleNavigate(link.page)} className="group/link inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                            <span className="w-0 group-hover/link:w-3 overflow-hidden transition-all duration-200">
                              <Icon name="ChevronRight" size={12} className="text-accent" />
                            </span>
                            {link.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* BOTTOM BAR */}
        <div className="border-t border-primary/10 bg-card/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-muted-foreground text-xs">
                &copy; {new Date().getFullYear()} Acebarter. {t('footer.rights')}
              </p>
              <div className="flex items-center gap-5">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Icon name="ShieldCheck" size={14} className="text-accent" />
                  <span>{t('footer.bankGrade')}</span>
                </div>
                <div className="w-px h-3 bg-primary/10" />
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Icon name="Lock" size={14} className="text-accent" />
                  <span>{t('footer.encryption')}</span>
                </div>
                <div className="hidden sm:block w-px h-3 bg-primary/10" />
                <div className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Icon name="Globe" size={14} className="text-accent" />
                  <span>{t('footer.globalCoverage')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
